import React from "react"
import { useParams } from "react-router-dom"

import Breadcrumb from "@/components/BreadcrumbNew"
import ActionsDropdown from "@/pages/Admin/TrackableLinks/ActionsDropdown"
import MetricCards from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/MetricCards"
import TrackableLinkInteractions from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions"
import TrackableLinkUTMMetrics from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics"
import TrackableLinkMetricsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkMetricsSkeleton"

import { Button, Grid, Tooltip, Typography } from "@material-ui/core"
import { Link as LinkIcon } from "@material-ui/icons"
import { Loading, Notification } from "@/components"

import { inboxAppUrl } from "@/config/url"
import { Trackablelink } from "@/protocols/trackableLinks"
import ErrorHandlerService from "@/services/ErrorHandler"

import useDidMount from "@/hooks/useDidMount"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import ApiService from "@/services/Api"
import copy from "copy-to-clipboard"
import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/styles"
import { mountTrackableLinkURL } from "@/utils/trackableLinks"

type TrackableLinkResponseData = {
	trackablelink: Trackablelink
	percentageOfConversationsTracked: number
}

const TrackableLinkMetrics: React.FC = () => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { triggerValidation } = useValidation()

	const [loading, setLoading] = React.useState(false)
	const [trackableLinkData, setTrackableLinkData] = React.useState<TrackableLinkResponseData>({
		trackablelink: {} as Trackablelink,
		percentageOfConversationsTracked: 0
	})

	const classes = useStyles()
	const redirectLinkToWhatsApp = mountTrackableLinkURL(trackableLinkData.trackablelink.hash, trackableLinkData.trackablelink.slug)

	const copyHandler = (content: string) => {
		copy(content)
		Notification.success({ message: "Link copiado com sucesso!" })
	}

	const handleGetTrackableLinkById = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkResponseData>(`/trackable-links/${trackableLinkId}`)
			setTrackableLinkData({
				trackablelink: data.trackablelink,
				percentageOfConversationsTracked: data.percentageOfConversationsTracked
			})
			setLoading(false)
		} catch (error) {
			setLoading(false)
			Notification.error({ message: "Ocorreu um erro ao buscar as métricas" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useDidMount(async () => {
		await handleGetTrackableLinkById()
	})

	return (
		<Loading
			customLoadingElement={<TrackableLinkMetricsSkeleton />}
			loading={loading}
		>
			<Grid
				container
				direction="column"
				spacing={5}
			>
				<Grid item>
					<Breadcrumb
						data={[
							{ name: "Links rastreáveis", pathname: "/admin/tools/trackable-links" },
							{ name: "Métricas", pathname: `/admin/tools/trackable-links/${trackableLinkData.trackablelink.id}/metrics` },
							{ name: `${trackableLinkData.trackablelink.title}`, pathname: `/admin/tools/trackable-links/${trackableLinkData.trackablelink.id}/metrics` }
						]}
					/>
				</Grid>

				<Grid item>
					<Grid container direction="column" spacing={1}>
						<Grid item>
							<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
								<Grid item>
									<Typography
										variant="h5"
										className={classes.titleText}
									>
										{trackableLinkData.trackablelink.title}
									</Typography>
								</Grid>
								<Grid item>
									<ActionsDropdown
										copyLinkUrl={redirectLinkToWhatsApp}
										trackableLinkId={trackableLinkData.trackablelink.id}
									/>
								</Grid>
							</Grid>
						</Grid>
						<Grid item>
							<Tooltip title="Copiar link">
								<Button
									variant="text"
									className={classes.copyButton}
									size="small"
									onClick={() => copyHandler(redirectLinkToWhatsApp)}
									startIcon={<LinkIcon fontSize="default"/>}
								>
									{`${inboxAppUrl.trackableLinkURL}/${trackableLinkData.trackablelink.hash}/${trackableLinkData.trackablelink.slug}`}
								</Button>
							</Tooltip>
						</Grid>
					</Grid>
				</Grid>

				<Grid item>
					<MetricCards
						clicksCount={trackableLinkData.trackablelink.consolidatedData?.clicksCount}
						sentMessagesCount={trackableLinkData.trackablelink.consolidatedData?.sentMessagesCount}
						percentageOfConversationsTracked={trackableLinkData.percentageOfConversationsTracked}
					/>
				</Grid>

				<Grid item>
					<Grid container direction="column" spacing={9}>
						<Grid item>
							<TrackableLinkInteractions />
						</Grid>
						<Grid item>
							<TrackableLinkUTMMetrics />
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Loading>
	)
}

export default TrackableLinkMetrics
